import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import type { MailContextMenuItem } from "@/features/shared/MailContextMenu";
import { MobileButton } from "@/mobile/components/ui/MobileButton";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  subject?: string;
  items: MailContextMenuItem[];
  onAction: (actionId: string) => void;
  onClose: () => void;
};

/** Feuille d’actions tactile (appui long sur une ligne). */
export function MobileMessageActionSheet({
  open,
  subject,
  items,
  onAction,
  onClose,
}: Props) {
  if (!open) return null;

  const colorItems = items.filter((item) => item.kind === "color");
  const mainItems = items.filter(
    (item) => item.kind !== "color" && item.kind !== "color-header" && item.kind !== "color-clear"
  );
  const clearColor = items.find((item) => item.id === "color:none");

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        aria-label="Fermer"
        onClick={onClose}
      />
      <div className="relative max-h-[70vh] overflow-hidden rounded-t-2xl bg-[var(--mail-surface)] shadow-xl">
        <div className="border-b border-[var(--mail-border-soft)] px-4 py-3">
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-[var(--mail-border)]" />
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--mail-muted)]">
            Options
          </p>
          {subject ? (
            <p className="mt-1 truncate text-sm font-medium text-[var(--mail-text)]">{subject}</p>
          ) : null}
        </div>
        <div className="mobile-sheet-footer max-h-[50vh] overflow-y-auto px-2 py-2">
          {mainItems.map((item) => {
            const Icon = item.icon;
            return (
              <MobileButton
                key={item.id}
                variant="ghost"
                disabled={item.disabled}
                className={cn(
                  "h-auto w-full justify-start gap-3 px-4 py-3 text-left text-sm",
                  item.danger && "text-red-600"
                )}
                onClick={() => {
                  if (item.disabled) return;
                  onAction(item.id);
                }}
              >
                {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
                <span>{item.label}</span>
              </MobileButton>
            );
          })}

          {colorItems.length > 0 ? (
            <div className="border-t border-[var(--mail-border-soft)] px-4 py-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--mail-muted)]">
                Couleur
              </p>
              <div className="flex flex-wrap gap-2">
                {colorItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    title={item.label}
                    aria-label={item.label}
                    onClick={() => onAction(item.id)}
                    className={cn(
                      "relative h-7 w-7 rounded-full border-2",
                      item.active
                        ? "border-[var(--mail-text)] ring-2 ring-[var(--mail-accent)]/35"
                        : "border-white/80"
                    )}
                    style={{ backgroundColor: item.colorSwatch }}
                  >
                    {item.active ? (
                      <Check
                        className="absolute inset-0 m-auto h-3.5 w-3.5 text-white drop-shadow-sm"
                        strokeWidth={3}
                        aria-hidden
                      />
                    ) : null}
                  </button>
                ))}
              </div>
              {clearColor ? (
                <button
                  type="button"
                  className="mt-2 text-xs text-[var(--mail-muted)] underline"
                  onClick={() => onAction(clearColor.id)}
                >
                  {clearColor.label}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
